package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.core.uuidV7
import com.dotdrives.model.*
import kotlinx.coroutines.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.*
import kotlin.time.Clock

@Serializable
data class PendingAdminOperation(
    val userId:String,val action:String,val orgId:String?,val input:JsonObject,val reason:String,val operationId:String,
    val recovery:AdminOperationRecovery=AdminOperationRecovery.RETRYABLE,
)

@Serializable
enum class AdminOperationRecovery { RETRYABLE, MANUAL_REVIEW }

interface AdminGateway {
    suspend fun identity():AdminIdentity
    suspend fun read(view:String,filter:JsonObject):JsonObject
    suspend fun mutate(operation:PendingAdminOperation):JsonObject
}
interface AdminOperationStore {
    fun load(userId:String):PendingAdminOperation?
    fun save(operation:PendingAdminOperation)
    fun clear(userId:String)
}
data class AdminUiState(
    val checking:Boolean=true,val identity:AdminIdentity?=null,val accessError:String?=null,
    val view:String="attention",val filter:JsonObject=buildJsonObject{},val data:JsonObject?=null,
    val loading:Boolean=false,val error:String?=null,val saving:Boolean=false,
    val pending:PendingAdminOperation?=null,val mutationError:String?=null,
    val feedback:String?=null,val outcome:JsonObject?=null,
)

class AdminController(
    private val gateway:AdminGateway,private val store:AdminOperationStore,scope:CoroutineScope,
    private val now:()->kotlin.time.Instant={ Clock.System.now() },
):DotController<AdminUiState>(AdminUiState(),scope) {
    private var generation=0L
    private var authGeneration=0L
    private var loadJob:Job?=null
    fun start(view:String,filter:JsonObject=buildJsonObject{}) {
        val authAttempt=++authGeneration
        scope.launch {
            update { it.copy(checking=true,accessError=null) }
            try {
                val who=gateway.identity()
                if(authAttempt!=authGeneration)return@launch
                if(who.expired(now())) error("expired")
                val pending=store.load(who.userId)?.takeIf { it.userId==who.userId }
                update { it.copy(checking=false,identity=who,pending=pending) }
                load(view,filter)
            } catch(e:CancellationException) { throw e }
            catch(e:Throwable) { if(authAttempt==authGeneration)deny("Staff access requires an explicit invitation and a confirmed account. Super admins can sign in with email/password or a magic link.") }
        }
    }
    fun deny(message:String="Your staff session ended. Sign in again.") {
        authGeneration++;generation++;loadJob?.cancel()
        update { AdminUiState(checking=false,accessError=message) }
    }
    fun load(view:String,filter:JsonObject=buildJsonObject{}) {
        val who=state.value.identity ?: return
        if(who.expired(now())) { deny();return }
        val mine=++generation
        loadJob?.cancel()
        update { it.copy(view=view,filter=filter,data=null,loading=true,error=null,outcome=null) }
        if(!who.isSuperAdmin && view in setOf("staff","config")) { update {it.copy(loading=false,error="Super admin access is required for this view.")};return }
        loadJob=scope.launch {
            try {
                val data=gateway.read(view,filter)
                if(mine==generation) update { it.copy(data=data,loading=false) }
            } catch(e:CancellationException) { throw e }
            catch(e:Throwable) {
                if(mine!=generation)return@launch
                if(e.isAdminAuthorizationFailure()) revalidateIdentity()
                else update { it.copy(loading=false,error="Could not load this view. Refresh to retry; sign in again if your access changed.") }
            }
        }
    }
    fun retry() { load(state.value.view,state.value.filter) }
    fun clearFeedback() { update { it.copy(feedback=null,outcome=null) } }
    fun perform(action:String,orgId:String?,input:JsonObject,reason:String) {
        val who=state.value.identity ?: return
        if(who.expired(now())) { deny();return }
        if(state.value.saving) return
        if(adminRequiresSuper(action)&&!who.isSuperAdmin) { update { it.copy(mutationError="A Super admin must perform this action.") };return }
        if(reason.trim().length !in 3..1000) { update { it.copy(mutationError="Enter a reason between 3 and 1,000 characters.") };return }
        val pending=state.value.pending
        if(pending!=null) {
            if(pending.action!=action||pending.orgId!=orgId||pending.input!=input||pending.reason!=reason) {
                update { it.copy(mutationError="Resolve the saved request before starting another action.") };return
            }
            submit(pending);return
        }
        val operation=PendingAdminOperation(who.userId,action,orgId,input,reason,uuidV7())
        try { store.save(operation) }
        catch(e:Throwable) { update { it.copy(mutationError="This browser could not save the request for recovery. Allow session storage before retrying.") };return }
        update { it.copy(pending=operation) }
        submit(operation)
    }
    fun recoveryReviewed() {
        val who=state.value.identity ?: return
        store.clear(who.userId)
        update { it.copy(pending=null,mutationError=null,feedback="Recovery copy closed after manual review.") }
    }
    fun retryPending() { state.value.pending?.takeIf { it.recovery==AdminOperationRecovery.RETRYABLE }?.let(::submit) }
    private fun submit(operation:PendingAdminOperation) {
        val who=state.value.identity ?: return
        if(operation.userId!=who.userId||who.expired(now())||state.value.saving||operation.recovery==AdminOperationRecovery.MANUAL_REVIEW) return
        update { it.copy(saving=true,mutationError=null,feedback=null,outcome=null) }
        scope.launch {
            try {
                val outcome=gateway.mutate(operation)
                // A failed clear preserves the same completed server operation for safe replay.
                store.clear(operation.userId)
                if(state.value.identity?.userId==operation.userId) update { it.copy(saving=false,pending=null,outcome=JsonObject(outcome+("_action" to JsonPrimitive(operation.action))),feedback=outcome.text("message")?:"Changes saved.") }
            } catch(e:CancellationException) { throw e }
            catch(e:Throwable) {
                if(e is WorkflowFailure && e.code=="reconciliation_required") {
                    val review=operation.copy(recovery=AdminOperationRecovery.MANUAL_REVIEW)
                    runCatching { store.save(review) }
                    if(state.value.identity?.userId==operation.userId) update { it.copy(saving=false,pending=review,mutationError=e.workflowMessage("This request needs manual review before its recovery copy can be closed.")) }
                    return@launch
                }
                val unsent = e is WorkflowFailure && e.code in setOf("invalid_request", "service_unavailable", "validation_failed")
                if(unsent) runCatching { store.clear(operation.userId) }
                if(state.value.identity?.userId==operation.userId) {
                    update { it.copy(saving=false,pending=if(unsent) null else it.pending,mutationError=e.workflowMessage("The request could not be confirmed. Retry the saved request; its operation ID is preserved.")) }
                    if(e.isAdminAuthorizationFailure()) revalidateIdentity()
                }
            }
        }
    }
    private fun revalidateIdentity() {
        val authAttempt=++authGeneration
        scope.launch {
            try {
                val who=gateway.identity()
                if(authAttempt!=authGeneration)return@launch
                if(who.expired(now())) { deny();return@launch }
                val forbidden=!who.isSuperAdmin && state.value.view in setOf("staff","config")
                update { it.copy(identity=who,checking=false,loading=false,error=if(forbidden)"Super admin access is required for this view." else "Your staff permissions changed. Refresh to continue.") }
            } catch(e:CancellationException) { throw e }
            catch(e:Throwable) { if(authAttempt==authGeneration)deny() }
        }
    }
}

private fun Throwable.isAdminAuthorizationFailure() = this is WorkflowFailure && code in setOf("forbidden","unauthorized")
